import React from "react";
import { Link } from "react-router-dom";
import { 
  Sparkles, 
  MapPin, 
  Phone, 
  Mail, 
  ShieldCheck, 
  Truck, 
  CheckCircle2, 
  Lock,
  ArrowRight,
  Facebook,
  Instagram,
  Youtube,
  Twitter,
  MessageCircle
} from "lucide-react";
import { useSiteSettings } from "@/context/SiteSettingsContext";

export default function Footer() {
  const { settings } = useSiteSettings();

  const categories = [
    { name: "Diamond Zari Sitara Lace", slug: "diamond-zari-sitara-lace" },
    { name: "Medium Size Diamond Sitara", slug: "medium-size-diamond-sitara" },
    { name: "Zero Size Diamond Sitara", slug: "zero-size-diamond-sitara" },
    { name: "Organza Lace", slug: "organza-lace" },
    { name: "Net Milky Lace", slug: "net-milky-lace" },
    { name: "Bridal Lace", slug: "bridal-lace" },
    { name: "Sequence Galla", slug: "sequence-galla" },
    { name: "Fashion Accessories", slug: "fashion-accessories" },
    { name: "Milky White Black Lace", slug: "milky-white-black-lace" },
    { name: "Milky Color Lace", slug: "milky-color-lace" }
  ];

  return (
    <footer className="bg-[#1C1917] text-[#FAF8F5] pt-16 pb-8 border-t-4 border-[#D4AF37]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Value Propositions Strip */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pb-12 border-b border-stone-800 text-stone-300">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37]">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold">100% Original China Import</h4>
              <p className="text-xs text-stone-400">Direct factory laces with premium diamond shine</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37]">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold">Complete Sets Only</h4>
              <p className="text-xs text-stone-400">Colors, Taan & Gaz pre-calculated per set</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37]">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold">Nationwide Delivery</h4>
              <p className="text-xs text-stone-400">Cash on Delivery across all cities in Pakistan</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37]">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold">Wholesale & Retail Supply</h4>
              <p className="text-xs text-stone-400">Special rates for boutiques, brands & retailers</p>
            </div>
          </div>
        </div>

        {/* Main Footer Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 py-12">
          
          {/* Brand Info */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#D4AF37]"></span>
              <span className="font-brand text-2xl font-bold tracking-wider text-white">
                ANWER <span className="text-[#D4AF37] font-serif italic">FANCY LACE</span>
              </span>
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#E6C687]">
              Pakistan's Premium Lace Brand
            </p>
            <p className="text-sm text-stone-400 leading-relaxed pr-4">
              We specialize in original China-made luxury fancy laces, diamond sitara laces, zari laces, organza laces, bridal lehenga borders, and premium fashion accessories for designers, boutiques, and fashion enthusiasts across Pakistan.
            </p>
            
            <div className="pt-2 text-xs space-y-2 text-stone-400">
              {settings.address && (
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#D4AF37] shrink-0" />
                  <span data-testid="footer-address">{settings.address}</span>
                </p>
              )}
              {settings.whatsapp_number && (
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#D4AF37] shrink-0" />
                  <span data-testid="footer-whatsapp">WhatsApp / Phone: {settings.whatsapp_number}</span>
                </p>
              )}
              {settings.email && (
                <p className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#D4AF37] shrink-0" />
                  <span data-testid="footer-email">Email: {settings.email}</span>
                </p>
              )}
            </div>

            {/* Social icons — only shown for URLs the admin has configured */}
            {(() => {
              const links = settings.social_links || {};
              const items = [
                { key: "facebook",  url: links.facebook,  icon: Facebook,  label: "Facebook"  },
                { key: "instagram", url: links.instagram, icon: Instagram, label: "Instagram" },
                { key: "tiktok",    url: links.tiktok,    icon: null,      label: "TikTok"    },
                { key: "youtube",   url: links.youtube,   icon: Youtube,   label: "YouTube"   },
                { key: "twitter",   url: links.twitter,   icon: Twitter,   label: "Twitter/X" },
                { key: "whatsapp",  url: links.whatsapp,  icon: MessageCircle, label: "WhatsApp" },
              ].filter((s) => s.url && s.url.trim());
              if (items.length === 0) return null;
              return (
                <div className="pt-3 flex flex-wrap items-center gap-2" data-testid="footer-social-icons">
                  {items.map((s) => {
                    const IconComp = s.icon;
                    return (
                      <a
                        key={s.key}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.label}
                        data-testid={`footer-social-${s.key}`}
                        className="w-9 h-9 rounded-full bg-[#2A2721] hover:bg-[#D4AF37] hover:text-[#1C1917] text-[#D4AF37] flex items-center justify-center transition"
                      >
                        {IconComp ? <IconComp className="w-4 h-4" /> : <span className="text-[11px] font-bold">TT</span>}
                      </a>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Categories 1 */}
          <div className="space-y-3">
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase border-b border-stone-800 pb-2">
              Popular Categories
            </h4>
            <ul className="space-y-2 text-xs text-stone-400">
              {categories.slice(0, 5).map((cat) => (
                <li key={cat.slug}>
                  <Link 
                    to={`/shop?category=${encodeURIComponent(cat.name)}`} 
                    className="hover:text-[#E6C687] transition flex items-center gap-1.5"
                  >
                    <ArrowRight className="w-3 h-3 text-stone-600" />
                    <span>{cat.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories 2 */}
          <div className="space-y-3">
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase border-b border-stone-800 pb-2">
              Specialized Laces
            </h4>
            <ul className="space-y-2 text-xs text-stone-400">
              {categories.slice(5).map((cat) => (
                <li key={cat.slug}>
                  <Link 
                    to={`/shop?category=${encodeURIComponent(cat.name)}`} 
                    className="hover:text-[#E6C687] transition flex items-center gap-1.5"
                  >
                    <ArrowRight className="w-3 h-3 text-stone-600" />
                    <span>{cat.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Care & Payment */}
          <div className="space-y-3">
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase border-b border-stone-800 pb-2">
              Payment & Wholesale
            </h4>
            <p className="text-xs text-stone-400 leading-relaxed">
              We offer flexible payment methods for your convenience across Pakistan:
            </p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              <span className="px-2.5 py-1 bg-stone-800 text-emerald-400 text-[11px] font-semibold rounded border border-stone-700">
                Cash on Delivery (COD)
              </span>
              <span className="px-2.5 py-1 bg-stone-800 text-[#E6C687] text-[11px] font-semibold rounded border border-stone-700">
                Meezan Bank Transfer
              </span>
              <span className="px-2.5 py-1 bg-stone-800 text-emerald-400 text-[11px] font-semibold rounded border border-stone-700">
                EasyPaisa
              </span>
              <span className="px-2.5 py-1 bg-stone-800 text-rose-400 text-[11px] font-semibold rounded border border-stone-700">
                JazzCash
              </span>
            </div>
            
            <div className="pt-3 space-y-2">
              <Link
                to="/wholesale"
                data-testid="footer-wholesale-link"
                className="inline-flex items-center gap-2 text-xs text-[#D4AF37] hover:text-white font-semibold underline"
              >
                <span>Bulk / Wholesale Inquiry Portal</span>
                <ArrowRight className="w-3 h-3" />
              </Link>
              <br />
              <Link
                to="/track-order"
                data-testid="footer-track-order-link"
                className="inline-flex items-center gap-2 text-xs text-[#E6C687] hover:text-white font-semibold underline"
              >
                <span>Track Your Order</span>
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 mt-4 border-t border-stone-800/80 flex flex-col sm:flex-row items-center justify-between text-xs text-stone-500 gap-4">
          <p>© {new Date().getFullYear()} Anwer Fancy Lace. All Rights Reserved. Pakistan's Premium Lace Brand.</p>
          <div className="flex items-center gap-6">
            <Link to="/about" className="hover:text-stone-300">About Us</Link>
            <Link to="/contact" className="hover:text-stone-300">Contact</Link>
            <Link to="/shop" className="hover:text-stone-300">All Products</Link>
            <Link to="/track-order" className="hover:text-[#E6C687]">Track Order</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
